import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { HomeView } from './components/HomeView';
import { AchievementsView } from './components/AchievementsView';
import { TechFeedsView } from './components/TechFeedsView';
import { GuestbookView } from './components/GuestbookView';
import { StocksView } from './components/StocksView';
import { AdminView } from './components/AdminView';
import { RegistrationApprovalView } from './components/RegistrationApprovalView';
import { PromptAnalyzerView } from './components/PromptAnalyzerView';
import { DossierModal } from './components/DossierModal';
import { AuthModal } from './components/AuthModal';
import SplashScreen from './components/SplashScreen';
import TermsModal from './components/TermsModal';
import { WeatherView } from './components/WeatherView';
import { FlightsView } from './components/FlightsView';
import { initAnalytics, trackPageView } from './lib/analytics';
import { useAuth } from './context/AuthContext';
import './App.css';

export default function App() {
  const [currentTab, setCurrentTab] = useState('home');
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(() => {
    // Show splash once per browser session.
    try { return !window.sessionStorage.getItem('rs_splash_seen'); } catch { return true; }
  });
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    initAnalytics();
    trackPageView('home');
    // Track home page load on backend
    import('axios').then(({ default: axios }) => {
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/home`).catch(() => {});
    });
  }, []);

  // If user logs out while on stocks/admin tab, send them home.
  useEffect(() => {
    if (currentTab === 'stocks' && !isAuthenticated) setCurrentTab('home');
    if (currentTab === 'prompt_analyzer' && !isAuthenticated) setCurrentTab('home');
    if (currentTab === 'admin' && !isAdmin) setCurrentTab('home');
    if (currentTab === 'admin_approvals' && !isAdmin) setCurrentTab('home');
    if (currentTab === 'flights' && !isAuthenticated) setCurrentTab('home');
  }, [currentTab, isAuthenticated, isAdmin]);

  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    trackPageView(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'home':
        return <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'achievements':
        return <AchievementsView onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'tech_feeds':
        return <TechFeedsView />;
      case 'useful_data':
        // Parent id → default landing = News (existing).
        return <TechFeedsView />;
      case 'weather':
        return <WeatherView />;
      case 'flights':
        return isAuthenticated
          ? <FlightsView />
          : <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'guestbook':
        return <GuestbookView />;
      case 'stocks':
        return isAuthenticated ? <StocksView /> : <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'prompt_analyzer':
        return isAuthenticated ? <PromptAnalyzerView /> : <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'admin':
        return isAdmin ? <AdminView /> : <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      case 'admin_approvals':
        return isAdmin ? <RegistrationApprovalView /> : <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
      default:
        return <HomeView setTab={handleTabChange} onOpenDossier={() => setIsDossierOpen(true)} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#030508] text-slate-100 flex flex-col justify-between relative" data-testid="app-root">
      {showSplash && (
        <SplashScreen
          onDone={() => {
            setShowSplash(false);
            try { window.sessionStorage.setItem('rs_splash_seen', '1'); } catch { /* ignore */ }
          }}
        />
      )}
      <div className="absolute top-0 inset-x-0 h-[450px] bg-gradient-to-b from-[#00f0ff]/5 via-transparent to-transparent pointer-events-none z-0" />
      <div className="absolute top-1/3 left-0 w-80 h-80 bg-[#9d4edd]/5 blur-3xl rounded-full pointer-events-none z-0" />

      <Header
        currentTab={currentTab}
        setTab={handleTabChange}
        onOpenAuth={() => setIsAuthOpen(true)}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8 md:py-12 z-10 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="border-t border-slate-900 bg-slate-950/60 py-8 px-4 md:px-6 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <span className="font-display font-extrabold text-sm text-[#f1f5f9] tracking-wider uppercase">
              RISHAB SALGOTRA
            </span>
            <p className="text-slate-500 text-xs font-mono">
              &copy; {new Date().getFullYear()} · Designed &amp; built by Rishab Salgotra &mdash; Dubai, UAE
            </p>
            <button
              onClick={() => setIsTermsOpen(true)}
              className="text-[10px] font-mono text-[#9d4edd] hover:text-[#c89cff] hover:underline transition-colors cursor-pointer"
              data-testid="btn-open-terms-footer"
            >
              View Terms &amp; Conditions →
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 font-mono text-[9px] text-slate-500 uppercase">
            <div className="flex items-center gap-1.5 border border-slate-900 bg-slate-950 px-2 py-1 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] signal-dot" />
              <span>TLS_1.3_ENCRYPTED</span>
            </div>
            <div className="flex items-center gap-1.5 border border-slate-900 bg-slate-950 px-2 py-1 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 signal-dot" />
              <span>FAB_NEXTGEN_NODE_DXB</span>
            </div>
            <div>
              <span>&copy; {new Date().getFullYear()} ALL RIGOURS PROTECTED</span>
            </div>
          </div>
        </div>
      </footer>

      <DossierModal isOpen={isDossierOpen} onClose={() => setIsDossierOpen(false)} />
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onOpenTerms={() => setIsTermsOpen(true)}
      />
      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </div>
  );
}
